import {
  type CompositionV3Ent,
  type CustomRoleFilter,
  EntKind,
  POSTING_SOURCE_IDS,
  type PostingsV3DataQuery,
  type PostingsV3DataQueryVariables,
  type PostingsV3RespData,
} from '@revelio/data-access';

import {
  type AllSelectionListsWithSkillTaxonomyKey,
  type DateRangeFilter,
  type Filter,
  FilterId,
  FilterType,
  type ListFilter,
  type TreeFilter,
} from '../../CustomDash/filters/types/filters';
import { buildPostingsV3VariablesFromEntities } from '../../CustomDash/filters/utils/getPostingsV3Variables';
import type { CompanyPostings } from './usePostingsComparison';

/** A `postings_v3` timeseries point: `id` is an absolute system-timeline week index. */
type PostingsV3MetricPoint = { id: number; val: number };

/**
 * Returns the latest value of a `postings_v3` metric timeseries, or `null` when
 * empty. Point ids are absolute week indices, so the max-`id` point is the most
 * recent — order-independent.
 */
export const getLatestPostingsV3MetricValue = (
  points: ReadonlyArray<PostingsV3MetricPoint> | null | undefined
): number | null => {
  if (!points || points.length === 0) return null;
  const latest = points.reduce((acc, point) =>
    point.id > acc.id ? point : acc
  );
  return latest.val;
};

/** A geography selection threaded through from a resolved profile. */
export interface PostingsV3GeoSelection {
  /** The geo numeric id (rcid/msaid/etc). */
  id: number;
  /** The geo level, matching a geography selection list level id. */
  levelId: 'region' | 'country' | 'msa' | 'state' | 'city';
}

/**
 * Translates the individual-potential posting inputs (a flat `Record`, not a
 * dashboard `Filter[]`) into the minimal `Filter[]` that
 * {@link buildPostingsV3VariablesFromEntities} expects, then delegates to it.
 * The company batch is passed as order-preserved `entities`; only the
 * role/seniority/geography secondary filters need translating.
 *
 * `defaultMetricMode: 'expected_hires'` puts the request in HIRES mode — the only
 * single-request mode that returns BOTH `active` and `hires` (NORMAL mode would
 * drop the `hires` column these consumers display).
 */
export const buildIndividualPotentialPostingsV3Variables = ({
  companyIds,
  customRole,
  seniorityId,
  geo,
  startDate,
  endDate,
  providerId = POSTING_SOURCE_IDS.unified,
}: {
  companyIds: number[];
  customRole?: CustomRoleFilter;
  seniorityId?: number | null;
  geo?: PostingsV3GeoSelection | null;
  startDate: string;
  endDate: string;
  providerId?: number;
}): PostingsV3DataQueryVariables => {
  const entities: CompositionV3Ent[] = companyIds.map((id) => ({
    EntID: id,
    EntType: EntKind.Company,
  }));

  const effectiveFilters: Filter[] = [];

  const roleSelections = (customRole?.entities ?? []).flatMap((entity) => {
    if (entity?.id == null || entity.levelId == null) return [];
    return [
      {
        id: String(entity.id),
        name: String(entity.id),
        // `CustomRoleFilter` levelIds (`role_k150`, `role_k1500`, …) are valid
        // role selection-list keys; the wire type is a bare string.
        levelId: entity.levelId as AllSelectionListsWithSkillTaxonomyKey,
      },
    ];
  });
  if (roleSelections.length > 0) {
    const roleFilter: TreeFilter = {
      id: FilterId.ROLE,
      type: FilterType.TREE,
      name: 'Role',
      selectionListIds: [],
      selections: roleSelections,
    };
    effectiveFilters.push(roleFilter);
  }

  if (seniorityId != null) {
    const seniorityFilter: ListFilter = {
      id: FilterId.SENIORITY,
      type: FilterType.LIST,
      name: 'Seniority',
      selectionListId: 'seniority',
      selections: [{ id: String(seniorityId), name: String(seniorityId) }],
    };
    effectiveFilters.push(seniorityFilter);
  }

  if (geo) {
    const geoFilter: TreeFilter = {
      id: FilterId.GEOGRAPHY,
      type: FilterType.TREE,
      name: 'Geography',
      selectionListIds: [],
      selections: [
        {
          id: String(geo.id),
          name: String(geo.id),
          levelId: geo.levelId as AllSelectionListsWithSkillTaxonomyKey,
        },
      ],
    };
    effectiveFilters.push(geoFilter);
  }

  // Individual-potential owns its trailing window, so emit an empty `DATE_RANGE`
  // (the builder's date pipeline needs the shape) and overwrite the dates with
  // the caller's explicit ones below.
  const dateFilter: DateRangeFilter = {
    id: FilterId.DATE_RANGE,
    type: FilterType.DATE_RANGE,
    name: 'Date range',
    min: null,
    max: null,
  };
  effectiveFilters.push(dateFilter);

  const vars = buildPostingsV3VariablesFromEntities({
    entities,
    effectiveFilters,
    defaultMetricMode: 'expected_hires',
  });

  return {
    req: {
      ...vars.req,
      filters: {
        ...vars.req.filters,
        provider: [providerId],
        start_date: startDate,
        end_date: endDate,
      },
    },
  };
};

type PostingsV3ResponseEntity = NonNullable<
  PostingsV3DataQuery['postings_v3']
>['entities'][number];

/**
 * Maps a single `postings_v3` response entity onto the `CompanyPostings` view
 * model, reading the LATEST point of each metric series. The response does NOT
 * echo the entity id (entities are keyed by request index), so the caller passes
 * in `companyIds[entity.index]`. `expectedHires` reads `metrics.hires` (v3's
 * rename of `expected_hires`), populated because the request is in HIRES mode.
 */
export const parsePostingsV3Entry = (
  entity: PostingsV3ResponseEntity,
  companyId: number
): CompanyPostings => ({
  companyId,
  shortName: entity.short_name ?? '',
  activePostings: getLatestPostingsV3MetricValue(entity.metrics?.active),
  expectedHires: getLatestPostingsV3MetricValue(
    (entity.metrics as PostingsV3RespData)?.hires
  ),
  timeToFill: getLatestPostingsV3MetricValue(entity.metrics?.time_to_fill),
  postingSalary: getLatestPostingsV3MetricValue(entity.metrics?.salary),
});
